import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
} from "@mui/material";
import {
  PROJECT_TYPE_OPTIONS,
  PROJECT_STATUS_OPTIONS,
  PROJECT_STAGE_OPTIONS,
} from "../types/crmTypes";

const ProjectForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    clientId: "",
    leadId: "",
    title: "",
    type: "wedding",
    category: "standard",
    status: "enquiry",
    stage: "pre_production",
    event: {
      mainDate: "",
      timezone: "Asia/Kolkata",
      venue: {
        name: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      functions: [],
    },
    package: {
      id: "",
      name: "",
      price: "",
      currency: "INR",
      services: [],
      deliverablesIncluded: [],
      details: {},
    },
    financials: {
      subtotal: 0,
      discount: 0,
      tax: 0,
      grandTotal: 0,
      paid: 0,
      pending: 0,
      currency: "INR",
    },
    notesSummary: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        clientId: initialData.clientId || "",
        leadId: initialData.leadId || "",
        title: initialData.title || "",
        type: initialData.type || "wedding",
        category: initialData.category || "standard",
        status: initialData.status || "enquiry",
        stage: initialData.stage || "pre_production",
        event: {
          mainDate: initialData.event?.mainDate || "",
          timezone: initialData.event?.timezone || "Asia/Kolkata",
          venue: {
            name: initialData.event?.venue?.name || "",
            address: initialData.event?.venue?.address || "",
            city: initialData.event?.venue?.city || "",
            state: initialData.event?.venue?.state || "",
            zipCode: initialData.event?.venue?.zipCode || "",
          },
          functions: initialData.event?.functions || [],
        },
        package: {
          id: initialData.package?.id || "",
          name: initialData.package?.name || "",
          price: initialData.package?.price || "",
          currency: initialData.package?.currency || "INR",
          services: initialData.package?.services || [],
          deliverablesIncluded: initialData.package?.deliverablesIncluded || [],
          details: initialData.package?.details || {},
        },
        financials: {
          subtotal: initialData.financials?.subtotal || 0,
          discount: initialData.financials?.discount || 0,
          tax: initialData.financials?.tax || 0,
          grandTotal: initialData.financials?.grandTotal || 0,
          paid: initialData.financials?.paid || 0,
          pending: initialData.financials?.pending || 0,
          currency: initialData.financials?.currency || "INR",
        },
        notesSummary: initialData.notesSummary || "",
      });
    }
  }, [initialData]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] = value;
      return updated;
    });

    if (errors[path]) setErrors((prev) => ({ ...prev, [path]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Project title is required";
    if (!formData.clientId.trim()) newErrors.clientId = "Client ID is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Divider>
          <Typography variant="caption">Project Info</Typography>
        </Divider>

        <TextField
          label="Project Title *"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          fullWidth
          disabled={loading}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client ID *"
              value={formData.clientId}
              onChange={(e) => handleChange("clientId", e.target.value)}
              error={!!errors.clientId}
              helperText={errors.clientId}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lead ID"
              value={formData.leadId}
              onChange={(e) => handleChange("leadId", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange("type", e.target.value)}
                label="Type"
                disabled={loading}
              >
                {PROJECT_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                label="Status"
                disabled={loading}
              >
                {PROJECT_STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select
                value={formData.stage}
                onChange={(e) => handleChange("stage", e.target.value)}
                label="Stage"
                disabled={loading}
              >
                {PROJECT_STAGE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Event</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Main Event Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.event.mainDate}
              onChange={(e) => handleChange("event.mainDate", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Venue Name"
              value={formData.event.venue.name}
              onChange={(e) => handleChange("event.venue.name", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          label="Venue Address"
          value={formData.event.venue.address}
          onChange={(e) => handleChange("event.venue.address", e.target.value)}
          fullWidth
          disabled={loading}
        />

        <Divider>
          <Typography variant="caption">Package & Finance</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Package Name"
              value={formData.package.name}
              onChange={(e) => handleChange("package.name", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Package Price"
              type="number"
              value={formData.package.price}
              onChange={(e) => handleChange("package.price", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Subtotal"
              type="number"
              value={formData.financials.subtotal}
              onChange={(e) =>
                handleChange("financials.subtotal", Number(e.target.value))
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Discount"
              type="number"
              value={formData.financials.discount}
              onChange={(e) =>
                handleChange("financials.discount", Number(e.target.value))
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Paid"
              type="number"
              value={formData.financials.paid}
              onChange={(e) =>
                handleChange("financials.paid", Number(e.target.value))
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Pending"
              type="number"
              value={formData.financials.pending}
              onChange={(e) =>
                handleChange("financials.pending", Number(e.target.value))
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          label="Notes Summary"
          multiline
          rows={3}
          value={formData.notesSummary}
          onChange={(e) => handleChange("notesSummary", e.target.value)}
          fullWidth
          disabled={loading}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Project"
                : "Create Project"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ProjectForm;
